import {
  ApiAIPromptStatus,
  ApiSimpleComponent,
  ApiComponentUISource,
  ApiComponentBlockCategory,
} from "@incendium/api";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  useTheme,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  Add,
  AutorenewOutlined,
  Widgets,
  ViewComfy,
  Code,
  GridView,
} from "@mui/icons-material";
import { useCallback, useMemo, useState } from "react";
import { useEditor } from "@grapesjs/react";
import Loading from "Components/Loading/Loading";
import { useLanderBlocks } from "features/lightningLanders/hooks/useLanderBlocks";
import { useLanderLandingComponents } from "features/lightningLanders/hooks/useLanderLandingComponents";
import { useSharedComponentBlocks } from "features/sharedComponentBlocks";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import NewBlockDialog from "features/lightningLanders/components/NewBlockDialog";
import produce from "immer";
import { sortByDate } from "Helpers/arrays";

type CategoryFilter = "all" | ApiComponentBlockCategory | "misc";

const CATEGORY_LABELS: Record<ApiComponentBlockCategory | "misc", string> = {
  [ApiComponentBlockCategory.UNSPECIFIED]: "Misc",
  [ApiComponentBlockCategory.STRUCTURAL_LAYOUTS]: "Structural Layouts",
  [ApiComponentBlockCategory.HERO_SECTIONS]: "Hero sections",
  [ApiComponentBlockCategory.PRODUCT_PURCHASE_BLOCK]: "Product Purchase Block",
  [ApiComponentBlockCategory.PRODUCT_MEDIA]: "Product Media",
  [ApiComponentBlockCategory.FEATURES_AND_BENEFITS]: "Features & Benefits",
  [ApiComponentBlockCategory.COMPARISONS]: "Comparisons",
  [ApiComponentBlockCategory.CTAS]: "CTAs",
  [ApiComponentBlockCategory.FAQS]: "FAQs",
  misc: "Misc",
};

const CATEGORY_OPTIONS: Array<{ value: CategoryFilter; label: string }> = [
  { value: "all", label: "All" },
  {
    value: ApiComponentBlockCategory.STRUCTURAL_LAYOUTS,
    label: CATEGORY_LABELS[ApiComponentBlockCategory.STRUCTURAL_LAYOUTS],
  },
  {
    value: ApiComponentBlockCategory.HERO_SECTIONS,
    label: CATEGORY_LABELS[ApiComponentBlockCategory.HERO_SECTIONS],
  },
  {
    value: ApiComponentBlockCategory.PRODUCT_PURCHASE_BLOCK,
    label: CATEGORY_LABELS[ApiComponentBlockCategory.PRODUCT_PURCHASE_BLOCK],
  },
  {
    value: ApiComponentBlockCategory.PRODUCT_MEDIA,
    label: CATEGORY_LABELS[ApiComponentBlockCategory.PRODUCT_MEDIA],
  },
  {
    value: ApiComponentBlockCategory.FEATURES_AND_BENEFITS,
    label: CATEGORY_LABELS[ApiComponentBlockCategory.FEATURES_AND_BENEFITS],
  },
  {
    value: ApiComponentBlockCategory.COMPARISONS,
    label: CATEGORY_LABELS[ApiComponentBlockCategory.COMPARISONS],
  },
  {
    value: ApiComponentBlockCategory.CTAS,
    label: CATEGORY_LABELS[ApiComponentBlockCategory.CTAS],
  },
  {
    value: ApiComponentBlockCategory.FAQS,
    label: CATEGORY_LABELS[ApiComponentBlockCategory.FAQS],
  },
  { value: "misc", label: "Misc" },
];

export const DYNAMIC_PRODUCT_GRID_TYPE = "dynamic-product-grid";

export type LightningLanderComponentsSidebarVariant = "blocks" | "components";

export interface LightningLanderComponentsSidebarProps {
  onOpenProductGridModal?: () => void;
  /** When "components", show landing components (for block editor). When "blocks", show blocks (for page editor). */
  variant?: LightningLanderComponentsSidebarVariant;
}

function LightningLanderComponentsSidebar(
  props?: LightningLanderComponentsSidebarProps,
): JSX.Element {
  const { onOpenProductGridModal, variant = "blocks" } = props ?? {};
  const editor = useEditor();
  const {
    blocks: customBlocks,
    setBlocks,
    loading: customBlocksLoading,
  } = useLanderBlocks();
  const { components: landingComponents, loading: landingComponentsLoading } =
    useLanderLandingComponents();
  const { blocks: sharedBlocks, loading: sharedBlocksLoading } =
    useSharedComponentBlocks();
  const theme = useTheme();
  const [customCategoryFilter, setCustomCategoryFilter] =
    useState<CategoryFilter>("all");
  const [sharedCategoryFilter, setSharedCategoryFilter] =
    useState<CategoryFilter>("all");
  const [newBlockDialogOpen, setNewBlockDialogOpen] = useState(false);

  const loading =
    variant === "components"
      ? landingComponentsLoading
      : customBlocksLoading || sharedBlocksLoading || landingComponentsLoading;

  const orderedLandingComponents = useMemo(() => {
    return sortByDate(landingComponents, "createdAt");
  }, [landingComponents]);

  const handleDragStart = useCallback(
    (e: React.DragEvent, component: ApiSimpleComponent) => {
      if (!editor) return;

      e.dataTransfer.setData(
        "application/json",
        JSON.stringify({
          type: "component",
          componentId: component.id,
          componentName: component.name,
          html: component.html || "",
          css: component.css || "",
          js: component.js || "",
          ...(component.grapeJs ? { grapeJs: component.grapeJs } : {}),
        }),
      );
    },
    [editor],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleProductGridDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ type: DYNAMIC_PRODUCT_GRID_TYPE }),
    );
  }, []);

  const getComponentIcon = (component: ApiSimpleComponent) => {
    if (component.uiSource === ApiComponentUISource.CODE) {
      return <Code color="secondary" />;
    }
    return <ViewComfy color="secondary" />;
  };

  const filterBlocksByCategory = useCallback(
    (blocks: ApiSimpleComponent[], filter: CategoryFilter) => {
      if (filter === "all") {
        return blocks;
      }
      if (filter === "misc") {
        return blocks.filter(
          (block) =>
            !block.category ||
            block.category === ApiComponentBlockCategory.UNSPECIFIED,
        );
      }
      return blocks.filter((block) => block.category === filter);
    },
    [],
  );

  const filteredCustomBlocks = useMemo(
    () =>
      sortByDate(
        filterBlocksByCategory(customBlocks, customCategoryFilter),
        "createdAt",
      ),
    [customBlocks, customCategoryFilter, filterBlocksByCategory],
  );

  const filteredSharedBlocks = useMemo(
    () =>
      sortByDate(
        filterBlocksByCategory(sharedBlocks, sharedCategoryFilter),
        "createdAt",
      ),
    [sharedBlocks, sharedCategoryFilter, filterBlocksByCategory],
  );

  if (loading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Loading />
      </Box>
    );
  }

  if (variant === "components") {
    return (
      <Stack sx={{ borderRight: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography
            variant="h6"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Widgets />
            Components
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag components into your block
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <AccordianChartBuilderSidebarBlock title="Your Components">
            {landingComponents.length === 0 ? (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No components yet. Create product cards, buttons, and other
                  components from the Components page.
                </Typography>
              </Box>
            ) : (
              <List dense>
                {orderedLandingComponents.map((comp) => (
                  <ListItem
                    key={comp.id}
                    sx={{
                      cursor: "grab",
                      mb: 1,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                        borderColor: theme.palette.primary.main,
                      },
                      "&:active": {
                        cursor: "grabbing",
                      },
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, comp)}
                    onDragOver={handleDragOver}
                  >
                    <ListItemIcon>{getComponentIcon(comp)}</ListItemIcon>
                    <ListItemText
                      primary={comp.name || "Unnamed Component"}
                      primaryTypographyProps={{
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </AccordianChartBuilderSidebarBlock>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack sx={{ borderRight: `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Widgets />
          Blocks
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag blocks and components into your landing page
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        <AccordianChartBuilderSidebarBlock
          title="Dynamic components"
          defaultClosed
        >
          <List dense>
            <ListItem
              component="div"
              draggable
              onDragStart={handleProductGridDragStart}
              onDragOver={handleDragOver}
              onClick={() => onOpenProductGridModal?.()}
              sx={{
                mb: 1,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                cursor: "grab",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                },
                "&:active": {
                  cursor: "grabbing",
                },
              }}
            >
              <ListItemIcon>
                <GridView color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary="Product grid"
                primaryTypographyProps={{
                  fontSize: "13px",
                  fontWeight: 500,
                }}
                secondary="Drag onto canvas or click to add a grid"
                secondaryTypographyProps={{ fontSize: "11px" }}
              />
            </ListItem>
          </List>
        </AccordianChartBuilderSidebarBlock>

        <AccordianChartBuilderSidebarBlock
          title="Your Components"
          defaultClosed
        >
          {landingComponents.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No components yet. Create product cards, buttons, and more from
                the Components page.
              </Typography>
            </Box>
          ) : (
            <List dense>
              {orderedLandingComponents.map((comp) => {
                const isPending =
                  comp.prompt?.status ===
                  ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING;
                return (
                  <ListItem
                    key={comp.id}
                    sx={{
                      cursor: isPending ? "default" : "grab",
                      mb: 1,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      opacity: isPending ? 0.85 : 1,
                      "&:hover": !isPending
                        ? {
                            backgroundColor: theme.palette.action.hover,
                            borderColor: theme.palette.primary.main,
                          }
                        : {},
                      "&:active": !isPending ? { cursor: "grabbing" } : {},
                    }}
                    draggable={!isPending}
                    onDragStart={(e) => !isPending && handleDragStart(e, comp)}
                    onDragOver={handleDragOver}
                  >
                    <ListItemIcon>
                      {isPending ? (
                        <AutorenewOutlined
                          sx={{
                            fontSize: 20,
                            color: "info.main",
                            animation: "spin 2s linear infinite",
                            "@keyframes spin": {
                              "0%": { transform: "rotate(0deg)" },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                      ) : (
                        getComponentIcon(comp)
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={comp.name || "Unnamed Component"}
                      primaryTypographyProps={{
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                      secondary={isPending ? "AI processing…" : undefined}
                      secondaryTypographyProps={{ fontSize: "11px" }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </AccordianChartBuilderSidebarBlock>

        <AccordianChartBuilderSidebarBlock title="Your Blocks" defaultClosed>
          {variant === "blocks" && (
            <Box sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setNewBlockDialogOpen(true)}
                fullWidth
                sx={{ mb: 1 }}
              >
                New block with AI
              </Button>
            </Box>
          )}
          <Box sx={{ mb: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              value={customCategoryFilter}
              onChange={(e) =>
                setCustomCategoryFilter(e.target.value as CategoryFilter)
              }
              label="Category"
            >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {filteredCustomBlocks.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No custom blocks available
              </Typography>
            </Box>
          ) : (
            <List dense>
              {filteredCustomBlocks.map((block) => {
                const isPending =
                  block.prompt?.status ===
                  ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING;
                return (
                  <ListItem
                    key={block.id}
                    sx={{
                      cursor: isPending ? "default" : "grab",
                      mb: 1,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      opacity: isPending ? 0.85 : 1,
                      "&:hover": !isPending
                        ? {
                            backgroundColor: theme.palette.action.hover,
                            borderColor: theme.palette.primary.main,
                          }
                        : {},
                      "&:active": !isPending ? { cursor: "grabbing" } : {},
                    }}
                    draggable={!isPending}
                    onDragStart={(e) => !isPending && handleDragStart(e, block)}
                    onDragOver={handleDragOver}
                  >
                    <ListItemIcon>
                      {isPending ? (
                        <AutorenewOutlined
                          sx={{
                            fontSize: 20,
                            color: "info.main",
                            animation: "spin 2s linear infinite",
                            "@keyframes spin": {
                              "0%": { transform: "rotate(0deg)" },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                      ) : (
                        getComponentIcon(block)
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={block.name || "Unnamed Block"}
                      primaryTypographyProps={{
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                      secondary={isPending ? "AI processing…" : undefined}
                      secondaryTypographyProps={{ fontSize: "11px" }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </AccordianChartBuilderSidebarBlock>

        <AccordianChartBuilderSidebarBlock
          title="Incendium Blocks"
          defaultClosed
        >
          <Box sx={{ mb: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              value={sharedCategoryFilter}
              onChange={(e) =>
                setSharedCategoryFilter(e.target.value as CategoryFilter)
              }
              label="Category"
            >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {filteredSharedBlocks.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No shared blocks available
              </Typography>
            </Box>
          ) : (
            <List dense>
              {filteredSharedBlocks.map((block) => (
                <ListItem
                  key={block.externalId || block.id}
                  sx={{
                    cursor: "grab",
                    mb: 1,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                      borderColor: theme.palette.primary.main,
                    },
                    "&:active": {
                      cursor: "grabbing",
                    },
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, block)}
                  onDragOver={handleDragOver}
                >
                  <ListItemIcon>{getComponentIcon(block)}</ListItemIcon>
                  <ListItemText
                    primary={block.name || "Unnamed Block"}
                    primaryTypographyProps={{
                      fontSize: "13px",
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </AccordianChartBuilderSidebarBlock>
      </Box>

      {variant === "blocks" && (
        <NewBlockDialog
          open={newBlockDialogOpen}
          onClose={() => setNewBlockDialogOpen(false)}
          aiOnly
          onConfirm={(name, aiComponent) => {
            if (aiComponent) {
              setBlocks((prev) =>
                produce(prev, (draft) => {
                  draft.push(aiComponent);
                }),
              );
            }
            setNewBlockDialogOpen(false);
          }}
        />
      )}
    </Stack>
  );
}

export default LightningLanderComponentsSidebar;
